import { useAudioPlayer } from 'react-use-audio-player';
import { useEffect, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import type { CallActivity } from './types';

/**
 * Playback for one call's recording, with the transcript able to drive it.
 *
 * `durationSeconds` stands in until the sound reports its own, so the scrub bar
 * has a scale before anything has loaded.
 */
export function useCallRecording(activity: CallActivity) {
  const [currentTime, setCurrentTime] = useState(0);
  const audio = useAudioPlayer();
  /** Where a clicked word wants the playhead, held until the sound can take it. */
  const pendingSeek = useRef<number | null>(null);
  const duration = audio.duration || activity.durationSeconds || 0;
  const progress = duration > 0 ? currentTime / duration : 0;

  const startRecording = (url: string, autoplay: boolean) => {
    audio.load(url, {
      autoplay,
      html5: true,
      format: 'mp3',
      onend: () => setCurrentTime(activity.durationSeconds ?? 0),
    });
  };

  const togglePlay = () => {
    if (audio.error || !activity.recordingUrl) {
      return;
    }

    if (audio.src !== activity.recordingUrl) {
      startRecording(activity.recordingUrl, true);
      return;
    }

    audio.togglePlayPause();
  };

  const seek = (seconds: number) => {
    if (!audio.isReady || audio.error) {
      return;
    }

    const clamped = Math.min(Math.max(0, seconds), duration || seconds);

    audio.seek(clamped);
    setCurrentTime(clamped);
  };

  /**
   * Clicking a word asks to hear it, not merely to move a playhead.
   *
   * It is also the first thing many people click, before the recording has been
   * loaded at all, and `seek` is inert until then. So load on demand, and jump
   * as soon as there is something to jump within.
   */
  const playFrom = (seconds: number) => {
    if (audio.error || !activity.recordingUrl) {
      return;
    }

    const clamped = Math.min(Math.max(0, seconds), duration || seconds);

    setCurrentTime(clamped);

    if (audio.src !== activity.recordingUrl) {
      /**
       * Deferred rather than seeked from a load callback, and loaded without
       * autoplay, because both of those race the playhead back to zero:
       * Howler locks a HTML5 sound while it starts, and a seek arriving during
       * that lock is queued behind the play it was meant to precede. Nothing
       * is audible until the seek has landed, so the first word heard is the
       * one that was clicked.
       */
      pendingSeek.current = clamped;
      startRecording(activity.recordingUrl, false);
      return;
    }

    audio.seek(clamped);

    if (!audio.isPlaying) {
      audio.play();
    }
  };

  useEffect(() => {
    if (pendingSeek.current === null || !audio.isReady) {
      return;
    }

    const seconds = pendingSeek.current;

    pendingSeek.current = null;
    audio.seek(seconds);
    audio.play();
  }, [audio]);

  useInterval(
    () => setCurrentTime(audio.getPosition()),
    audio.isPlaying ? 250 : null,
  );

  return {
    currentTime,
    duration,
    progress,
    isPlaying: audio.isPlaying,
    isSeekable: audio.isReady && !audio.error,
    hasError: Boolean(audio.error),
    togglePlay,
    seek,
    playFrom,
  };
}
